import { useCallback, useEffect, useRef, useState } from 'react'

interface UseAutoLoadAtTopParams {
  hasMoreMessages: boolean
  isFetchingMoreMessages: boolean
  conversationId?: string
  onLoadMore?: () => void
}

/**
 * Triggers pagination when the user scrolls to the top of the chat list.
 * Ignores the initial atTop state on mount — only fires after the user has
 * scrolled away from the top at least once, proving it is a real scroll action.
 */
export const useAutoLoadAtTop = ({
  hasMoreMessages,
  isFetchingMoreMessages,
  conversationId,
  onLoadMore,
}: UseAutoLoadAtTopParams) => {
  const [isAtTop, setIsAtTop] = useState(false)
  const hasScrolledAwayRef = useRef(false)
  const prevConversationIdRef = useRef(conversationId)

  if (prevConversationIdRef.current !== conversationId) {
    hasScrolledAwayRef.current = false
    prevConversationIdRef.current = conversationId
  }

  useEffect(() => {
    if (!isAtTop) {
      hasScrolledAwayRef.current = true
      return
    }

    if (hasScrolledAwayRef.current && hasMoreMessages && !isFetchingMoreMessages) {
      onLoadMore?.()
    }
  }, [isAtTop, hasMoreMessages, isFetchingMoreMessages, onLoadMore])

  const handleAtTopStateChange = useCallback((atTop: boolean) => {
    setIsAtTop(atTop)
  }, [])

  return { handleAtTopStateChange }
}
